'use client';

import { Dialog, Tabs, Text, Stack, Badge } from '@backyard-ui/core';
import { useMediaQuery } from '@backyard-ui/hooks';

import { algolia } from '@/infra/tracking/algolia';
import { useDefaultStore } from '@/presenters/store/catalog/default';

import { PaymentMethods, BankslipContent, PixContent } from './components';

type PaymentMethodsModalProps = {
  currentSellerId?: string;
};

export default function PaymentMethodsModal(
  props: Readonly<PaymentMethodsModalProps>
) {
  const { currentSellerId = 'leroy' } = props;

  const paymentMethods = useDefaultStore((store) => store.paymentMethods);

  const isDesktop = useMediaQuery('md');

  const currentPaymentMethod = paymentMethods.find(
    (item) => item.sellerId === currentSellerId
  );

  if (!currentPaymentMethod) {
    return null;
  }

  const { creditCard, branded, brandedPro, bankSlip, pix } =
    currentPaymentMethod;

  return (
    <Dialog.Root size="3xl" isCentered closeOnOverlayClick hasScroll>
      <Dialog.Trigger
        data-cy="payment-methods-trigger"
        onClick={() => algolia.sendPdpClickEvent()}
      >
        <Text size="md" color="green-700" weight="bold" decoration="underline">
          Conheça mais formas de pagamento
        </Text>
      </Dialog.Trigger>

      <Dialog.Content>
        <Dialog.CloseButton />
        <Dialog.Title>
          <Text size="xl" color="black" weight="bold">
            Formas de pagamento
          </Text>
        </Dialog.Title>

        <Dialog.Description>
          <Tabs.Root defaultValue="credit-card">
            <Tabs.List aria-label="payment-methods">
              {creditCard && (
                <Tabs.Trigger value="credit-card">
                  <Text size={isDesktop ? 'lg' : 'md'}>Cartão de Crédito</Text>
                </Tabs.Trigger>
              )}
              {branded && (
                <Tabs.Trigger value="branded-card">
                  <Stack
                    spacing="1"
                    align="baseline"
                    direction={{ initial: 'column', md: 'row' }}
                  >
                    <Text size={isDesktop ? 'lg' : 'md'}>Cartão Celebre</Text>
                    {Boolean(branded.discountPercentage) && (
                      <Badge appearance="primary">
                        <span className="whitespace-nowrap">
                          - {branded.discountPercentage}%
                        </span>
                      </Badge>
                    )}
                  </Stack>
                </Tabs.Trigger>
              )}
              {brandedPro && (
                <Tabs.Trigger value="brandedPro-card">
                  <Stack
                    spacing="1"
                    align="baseline"
                    direction={{ initial: 'column', md: 'row' }}
                  >
                    <Text size={isDesktop ? 'lg' : 'md'}>Celebre Pro </Text>
                    {Boolean(brandedPro.discountPercentage) && (
                      <Badge appearance="primary">
                        <span className="whitespace-nowrap">
                          - {brandedPro.discountPercentage}%
                        </span>
                      </Badge>
                    )}
                  </Stack>
                </Tabs.Trigger>
              )}
              {bankSlip && (
                <Tabs.Trigger value="bankslip">
                  <Stack
                    spacing="1"
                    align="baseline"
                    direction={{ initial: 'column', md: 'row' }}
                  >
                    <Text size={isDesktop ? 'lg' : 'md'}>Boleto </Text>
                    {Boolean(bankSlip.discountPercentage) && (
                      <Badge appearance="primary">
                        <span className="whitespace-nowrap">
                          - {bankSlip.discountPercentage}%
                        </span>
                      </Badge>
                    )}
                  </Stack>
                </Tabs.Trigger>
              )}
              {pix && (
                <Tabs.Trigger value="pix">
                  <Stack
                    spacing="1"
                    align="baseline"
                    direction={{ initial: 'column', md: 'row' }}
                  >
                    <Text size={isDesktop ? 'lg' : 'md'}>Pix </Text>
                    {Boolean(pix.discountPercentage) && (
                      <Badge appearance="primary">
                        <span className="whitespace-nowrap">
                          - {pix.discountPercentage}%
                        </span>
                      </Badge>
                    )}
                  </Stack>
                </Tabs.Trigger>
              )}
            </Tabs.List>

            {creditCard && (
              <Tabs.Content value="credit-card">
                <PaymentMethods
                  price={creditCard.price}
                  installments={creditCard.installments}
                />
              </Tabs.Content>
            )}
            {branded && (
              <Tabs.Content value="branded-card">
                <PaymentMethods
                  price={branded.price}
                  installments={branded.installments}
                />
              </Tabs.Content>
            )}
            {brandedPro && (
              <Tabs.Content value="brandedPro-card">
                <PaymentMethods
                  price={brandedPro.price}
                  installments={brandedPro.installments}
                />
              </Tabs.Content>
            )}
            {bankSlip && (
              <Tabs.Content value="bankslip">
                <Stack direction="column" spacing="4">
                  <PaymentMethods
                    price={bankSlip.price}
                    installments={bankSlip.installments}
                  />
                  <BankslipContent />
                </Stack>
              </Tabs.Content>
            )}
            {pix && (
              <Tabs.Content value="pix">
                <Stack direction="column" spacing="4">
                  <PaymentMethods
                    price={pix.price}
                    installments={pix.installments}
                  />
                  <PixContent />
                </Stack>
              </Tabs.Content>
            )}
          </Tabs.Root>
        </Dialog.Description>
      </Dialog.Content>
    </Dialog.Root>
  );
}
